// src/components/common/Footer.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';

/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  white:      '#ffffff',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray700:    '#374151',
  gray800:    '#1f2937',
  gray900:    '#111827',
};

/* ── Facebook SVG (react-icons not needed) ── */
const IconFacebook = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const IconInstagram = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const IconWhatsApp = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

/* ── SocialBtn ── */
const SocialBtn = ({ href, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 42, height: 42, borderRadius: '50%',
        backgroundColor: hov ? C.amber : C.gray800,
        color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
        textDecoration: 'none',
        transform: hov ? 'scale(1.14) translateY(-3px)' : 'scale(1)',
        boxShadow: hov ? '0 8px 20px rgba(217,119,6,0.38)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      {children}
    </a>
  );
};

/* ── FooterLink (router) ── */
const FooterLink = ({ to, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'block', textDecoration: 'none',
        fontSize: 14, color: hov ? C.amber : C.gray400,
        transform: hov ? 'translateX(6px)' : 'translateX(0)',
        transition: 'color 0.25s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        marginBottom: 10,
      }}
    >
      {children}
    </Link>
  );
};

/* ── ContactRow ── */
const ContactRow = ({ Icon, href, children }) => {
  const [hov, setHov] = useState(false);
  const inner = (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14,
        color: hov ? C.amber : C.gray400,
        transform: hov ? 'translateX(4px)' : 'translateX(0)',
        transition: 'color 0.25s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        cursor: href ? 'pointer' : 'default',
      }}
    >
      <Icon size={15} color={C.amber} style={{ marginTop: 3, flexShrink: 0 }} />
      <span style={{ fontSize: 13.5, lineHeight: 1.6 }}>{children}</span>
    </div>
  );
  if (href) {
    return (
      <a href={href} target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        style={{ textDecoration: 'none' }}>
        {inner}
      </a>
    );
  }
  return inner;
};

/* ── ArthantoLink ── */
const ArthantaLink = () => {
  const [hov, setHov] = useState(false);
  return (
    <a
      href="https://arthantasolutions.com"
      target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        color: hov ? C.amber : 'rgba(255,255,255,0.75)',
        fontWeight: 600, textDecoration: 'none',
        transition: 'color 0.25s ease',
      }}
    >
      Arthanta Solutions
    </a>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const Footer = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

        .footer-col-divider {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.07);
          margin: 0;
        }
      `}</style>

      <footer style={{ backgroundColor: C.gray900, color: C.white, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── Main content ── */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '72px 24px 56px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 48,
          }}>

            {/* ── Col 1: Brand ── */}
            <div>
              <img
                src="/images/logo.png" alt="Dewanta Villa Management"
                style={{ height: 44, width: 'auto', objectFit: 'contain', marginBottom: 18, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
              <h3 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 20, fontWeight: 700, color: C.amber, marginBottom: 12,
              }}>
                Dewanta Villas<br />Management
              </h3>
              <p style={{ fontSize: 13.5, color: C.gray400, lineHeight: 1.8, marginBottom: 24, maxWidth: 260 }}>
                Trusted solution for villa and property management in Bali. We provide high-quality services to maximize your investment.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <SocialBtn href="https://web.facebook.com/profile.php?id=61577924980552&mibextid=wwXIfr&rdid=47byqMYDvjGZQp3k&share_url=https%3A%2F%2Fweb.facebook.com%2Fshare%2F1BmEZPeYKK%2F%3Fmibextid%3DwwXIfr%26_rdc%3D1%26_rdr">
                  <IconFacebook />
                </SocialBtn>
                <SocialBtn href="https://www.instagram.com/dewantavillas.management/?hl=en">
                  <IconInstagram />
                </SocialBtn>
                <SocialBtn href="https://wa.me/6287785686777">
                  <IconWhatsApp />
                </SocialBtn>
              </div>
            </div>

            {/* ── Col 2: Quick Links ── */}
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: C.amber, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>
                Navigation
              </h4>
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/villas">Villas</FooterLink>
              <FooterLink to="/business">Business Packages</FooterLink>
              <FooterLink to="/services">Services</FooterLink>
              <FooterLink to="/profile">About Us</FooterLink>
              <FooterLink to="/contact">Contact Us</FooterLink>
            </div>

            {/* ── Col 3: Services ── */}
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: C.amber, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>
                Our Services
              </h4>
              {['Property Management', 'Marketing & Promotion', 'Guest Services', 'Dynamic Pricing', '24/7 Support', 'Revenue Reporting'].map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: C.amber, flexShrink: 0 }} />
                  <span style={{ fontSize: 13.5, color: C.gray400 }}>{s}</span>
                </div>
              ))}
            </div>

            {/* ── Col 4: Contact ── */}
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: C.amber, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>
                Contact Us
              </h4>
              <ContactRow Icon={MapPin}>
                Jl Bypass Darma Giri Buruan<br />Blahbatuh Gianyar, Bali, Indonesia
              </ContactRow>
              <ContactRow Icon={Phone} href="tel:+6287785686777">
                +62 877-8568-6777
              </ContactRow>
              <ContactRow Icon={Mail} href="mailto:dewantavillas.management@gmail.com">
                dewantavillas.management@gmail.com
              </ContactRow>
              <ContactRow Icon={MessageCircle} href="https://wa.me/6287785686777">
                WhatsApp: +62 877-8568-6777
              </ContactRow>
            </div>

          </div>
        </div>

        {/* ── Divider ── */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

        {/* ── Bottom bar ── */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: 12,
          }}>
            {/* Left: copyright villa */}
            <p style={{ fontSize: 13, color: C.gray500, margin: 0 }}>
              © {new Date().getFullYear()} Dewanta Villas Management. All rights reserved.
            </p>

            {/* Right: powered by */}
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.38)', margin: 0 }}>
              Developed & Powered by{' '}
              <ArthantaLink />
            </p>
          </div>
        </div>

      </footer>
    </>
  );
};

export default Footer;